/**
 * "What's new" announcements.
 *
 * To ship another popup, add an object at the top of APP_UPDATES with a new
 * id. On the next launch, every device that has not seen that id gets the
 * popup, on the platforms listed. Older entries stay in Nexus Connect at the
 * top of the store, and anyone can open them again.
 *
 * Set released to false to park a popup in What's new before it ships.
 * It shows in the grid and does not pop on launch until released is true.
 *
 * Preview the newest popup on any platform with ?show_update=1.
 */

export type AppUpdatePlatform = 'ios' | 'android' | 'web';

export interface AppUpdateSlide {
  title: string;
  body: string;
}

export interface AppUpdate {
  /** Unique per announcement. A new id shows the popup again. */
  id: string;
  /** Ship date, YYYY-MM-DD. Shown on the What's new cards. */
  releasedOn: string;
  /**
   * False keeps it in the What's new grid and off the launch popup.
   * Omit or set true when the popup should start showing on open.
   */
  released?: boolean;
  /** Platforms that see the popup on launch. The archive shows every entry. */
  platforms: AppUpdatePlatform[];
  /** Short badge above the title, e.g. the release name. */
  eyebrow: string;
  /** Looping, muted video shown above the slides. Path under /public. */
  video?: string;
  slides: AppUpdateSlide[];
  /** Label on the last slide's button. */
  doneLabel: string;
}

/** Newest first. The launch popup uses the first entry this device has not seen. */
export const APP_UPDATES: AppUpdate[] = [
  {
    id: '2026-11-rewards',
    releasedOn: '2026-11-02',
    released: false,
    platforms: ['ios'],
    eyebrow: "What's new",
    video: '/updates/nexus-rewards-mobile.mp4',
    slides: [
      {
        title: 'Customers earn points when they pay',
        body: 'Turn on rewards and every paid invoice earns points. You set the spend ranges, and how many points each dollar in that range is worth.',
      },
      {
        title: 'You decide what the points buy',
        body: 'Add the rewards they can redeem: dollars off, a percent off, or wallet credit. You set how many points each one costs.',
      },
      {
        title: 'They spend them on the next booking',
        body: 'Customers see their points in their booking account and use them when they book again. A refund adjusts the points. Set it up under Marketing, Rewards Program.',
      },
    ],
    doneLabel: 'Done',
  },
  {
    id: '2026-10-agents',
    releasedOn: '2026-10-23',
    platforms: ['ios'],
    eyebrow: "What's new",
    video: '/updates/nexus-agents-mobile.mp4',
    slides: [
      {
        title: 'Your AI team is here',
        body: 'Nexus now runs a team of AI agents for your business. Ask for anything in plain words and they do the work inside the app: bookings, invoices, customers, reports.',
      },
      {
        title: 'Specialists that work on their own',
        body: 'Your Marketing manager answers comments and reviews and keeps your social posts going. Your Website agent edits your site. Build your own specialists for the jobs you repeat.',
      },
      {
        title: 'You stay in control',
        body: 'Anything that moves money or changes a setting arrives as an Approve card. Routines run on a schedule and report back. Find it all under Agents.',
      },
    ],
    doneLabel: 'Done',
  },
];

export function appUpdateById(id: string): AppUpdate | null {
  return APP_UPDATES.find((update) => update.id === id) ?? null;
}

/** Newest update this device should see on launch, or null when it is caught up. */
export function pendingAppUpdate(platform: AppUpdatePlatform, force = false): AppUpdate | null {
  const live = APP_UPDATES.filter((update) => update.released !== false);
  if (force) return live[0] ?? null;
  return live.find((update) => update.platforms.includes(platform) && !hasSeenAppUpdate(update.id)) ?? null;
}

export function formatAppUpdateDate(releasedOn: string): string {
  const [year, month, day] = releasedOn.split('-').map(Number);
  if (!year || !month || !day) return releasedOn;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const SEEN_PREFIX = 'nexus_update_seen_';

export function hasSeenAppUpdate(id: string): boolean {
  try {
    return localStorage.getItem(SEEN_PREFIX + id) === '1';
  } catch {
    return true;
  }
}

export function markAppUpdateSeen(id: string): void {
  try {
    localStorage.setItem(SEEN_PREFIX + id, '1');
  } catch {
    // Private mode or storage disabled: the popup shows again next launch.
  }
}
